import { TrashIcon } from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import PageHeading from "../../../components/common/PageHeading";
import { Text } from "../../../components/common/Typography";
import Button from "../../../components/form/Button";
import Input from "../../../components/form/Input";
import { ROUTE_DESTINATIONS } from "../../../constants/routes";
import {
  DestinationStatusEnum,
  DestinationTabs,
  DestinationTypeEnum,
} from "../../../enums/destination";
import { ShopifyDestination } from "../../../models/destination";
import TemplateContainer from "../../TemplateForm/components/common/TemplateContainer";
import { mask, secretKeyMask } from "../Destination";
import { StatusSelector } from "../components/common/StatusSelector";
import DestinationSaveModal from "../components/modals/DestinationSaveModal";
import ShopifyBlogIdGuideModal from "../components/modals/ShopifyBlogIdGuideModal";
import ShopifyGuideModal from "../components/modals/ShopifyGuideModal";

interface ShopifyProps {
  destination: any;
  isTestLoading: boolean;
  isTestError: boolean;
  testErrorMessage: any;
  showSaveModal: boolean;
  testSuccessMessage: string;
  isSaving: boolean;
  onSaveSuccess: () => void;
  handleTest: (formValues: any) => void;
  handleDelete: () => void;
  handleSubmit: (formValues: any) => void;
}

const shopifySchema = Yup.object().shape({
  name: Yup.string().required("Destination name is required"),
  destination: Yup.object().shape({
    store_name: Yup.string().required("Shopify store name is required"),
    blog_id: Yup.string().required("Blog ID is required"),
    secret_key: Yup.string()
      .required("App password is required")
      .test(
        "no-asterisk",
        "Please verify your app password",
        (value) => !value || !value.includes(secretKeyMask)
      ),
    author: Yup.string()
      .matches(/^[^0-9]*$/, "Author name cannot contain numbers")
      .nullable(),
  }),
});

const initialValues: ShopifyDestination = {
  name: "",
  destination_type_id: DestinationTypeEnum.SHOPIFY,
  destination: {
    store_name: "",
    author: "",
    blog_id: "",
    secret_key: "",
    status: DestinationStatusEnum.HIDDEN,
  },
};

const Shopify: React.FC<ShopifyProps> = ({
  destination,
  isTestLoading,
  isTestError,
  testErrorMessage,
  showSaveModal,
  testSuccessMessage,
  isSaving,
  onSaveSuccess,
  handleTest,
  handleSubmit,
  handleDelete,
}) => {
  const navigate = useNavigate();

  const [formValues, setFormValues] =
    useState<ShopifyDestination>(initialValues);
  const [showAccessTokenGuide, setShowAccessTokenGuide] = useState(false);
  const [showBlogIdGuide, setShowBlogIdGuide] = useState(false);

  useEffect(() => {
    if (destination) {
      setFormValues(
        new ShopifyDestination({
          ...destination,
          destination: {
            ...destination.destination,
            secret_key: `${secretKeyMask}${destination.destination.secret_key}`,
          },
        })
      );
    }
  }, [destination]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(ROUTE_DESTINATIONS + `?tab=${DestinationTabs.ADDED}`);
    }
  };

  return (
    <>
      <Formik
        initialValues={formValues}
        enableReinitialize={true}
        onSubmit={(values: ShopifyDestination) => {
          handleSubmit(values);
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={shopifySchema}
      >
        {({ values, errors, validateForm, setFieldValue }) => (
          <Form
            className="flex flex-col gap-6 flex-wrap transition-all duration-200"
            noValidate
          >
            <TemplateContainer
              className="max-w-[60.5rem] rounded"
              style={{ padding: 0 }}
            >
              <div className="py-4 px-4 sm:px-6 relative bg-gray-50 rounded-t-lg">
                <PageHeading heading="Configure Shopify" />
                <Text className="text-gray-500 text-sm">
                  Provide your account details to establish a connection
                </Text>
                {formValues?.destination_id && (
                  <Button
                    variant="light-danger"
                    tooltip="Delete wordpress connection"
                    className="group hover:border-red-500 hover:bg-red-50 absolute top-4 right-3"
                    onClick={handleDelete}
                  >
                    <TrashIcon className="size-5 text-red-500" />
                  </Button>
                )}
              </div>
              <div className="bg-gray-200 h-px w-full" />
              <div className="py-4 px-4 sm:px-6 flex flex-col gap-6 mb-4">
                <div className="flex flex-col md:flex-row gap-6">
                  <Input
                    id="name"
                    label="Destination Name"
                    name="name"
                    type="text"
                    required
                    placeholder="Name your destination"
                    className="max-w-md"
                    tooltip="Name your destination (e.g., My Shopify Site)"
                    value={values.name}
                    errorMessage={errors?.name}
                    errorPosition="bottom-left"
                    onChange={(e: any) => setFieldValue("name", e.target.value)}
                  />

                  <Input
                    id="store_name"
                    label="Store Name"
                    name="destination.store_name"
                    type="text"
                    required
                    placeholder="Name of your Shopify store (e.g. 6zabnw-ea)"
                    className="max-w-md"
                    tooltip='The Shopify store name (e.g. in https://admin.shopify.com/store/6zabnw-ea/content, the store name is "6zabnw-ea").'
                    value={values.destination.store_name}
                    errorMessage={errors?.destination?.store_name}
                    errorPosition="bottom-left"
                    onChange={(e: any) =>
                      setFieldValue("destination.store_name", e.target.value)
                    }
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-10 md:gap-5 mb-4">
                  <div className="relative w-full">
                    <Input
                      id="blog_id"
                      label="Blog ID"
                      name="blog_id"
                      type="text"
                      required
                      placeholder="Blog ID"
                      className="max-w-md"
                      tooltip="The numeric ID of your Shopify blog where articles will be published."
                      value={values.destination.blog_id}
                      errorMessage={errors?.destination?.blog_id}
                      errorPosition="bottom-left"
                      fontMono
                      onChange={(e: any) => {
                        setFieldValue("destination.blog_id", e.target.value);
                      }}
                    />
                    <Button
                      paddingZero
                      type="button"
                      variant="transparent-primary"
                      className="absolute mt-1"
                      onClick={() => setShowBlogIdGuide(true)}
                    >
                      How to get your blog ID
                    </Button>
                  </div>
                  <div className="relative w-full">
                    <Input
                      id="secret_key"
                      label="Access Token"
                      name="secret_key"
                      type="text"
                      required
                      placeholder="Access Token"
                      className="max-w-md"
                      tooltip="Shopify Admin API access token."
                      value={values.destination.secret_key}
                      errorMessage={errors?.destination?.secret_key}
                      errorPosition="bottom-left"
                      fontMono
                      onChange={(e: any) => {
                        setFieldValue("destination.secret_key", e.target.value);
                      }}
                      onFocus={(e: any) => {
                        if (e.target.value.includes(mask)) {
                          setFieldValue("destination.secret_key", "");
                        }
                      }}
                    />
                    <Button
                      paddingZero
                      type="button"
                      variant="transparent-primary"
                      className="absolute mt-1"
                      onClick={() => setShowAccessTokenGuide(true)}
                    >
                      How to generate
                    </Button>
                  </div>
                </div>
                <StatusSelector
                  status={values?.destination.status}
                  onChange={(status: string) =>
                    setFieldValue("destination.status", status)
                  }
                  platform={DestinationTypeEnum.SHOPIFY}
                />
                <Input
                  id="author"
                  label="Author Name"
                  name="destination.author"
                  type="text"
                  tooltip="Specify the Shopify author name to attribute to articles."
                  placeholder="Optional. Shopify Author Name. If blank, it will be set as 'Admin'"
                  className="max-w-md"
                  value={values?.destination?.author ?? ""}
                  errorMessage={errors?.destination?.author}
                  errorPosition="bottom-left"
                  onChange={(e: any) => {
                    setFieldValue("destination.author", e.target.value);
                  }}
                  onKeyDown={(e: any) => {
                    if (/\d/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            </TemplateContainer>
            {isTestError && (
              <div className="text-red-500 text-sm my-1">
                {testErrorMessage?.data?.detail ??
                  "Please check your settings."}
              </div>
            )}
            <div className="flex items-center justify-between max-w-5xl">
              <div className="flex items-center gap-6">
                <Button
                  variant="primary"
                  className="min-w-[8.75rem]"
                  onClick={() => {
                    validateForm().then((formErrors) => {
                      if (Object.keys(formErrors).length === 0) {
                        handleTest(values);
                      }
                    });
                  }}
                  loading={isTestLoading}
                >
                  Test Connection
                </Button>
                <Button
                  variant="transparent"
                  onClick={handleGoBack}
                >
                  Cancel
                </Button>
              </div>
            </div>
            {showSaveModal && (
              <DestinationSaveModal
                message={testSuccessMessage}
                loading={isSaving}
                onSave={() => handleSubmit(values)}
                onRequestClose={onSaveSuccess}
              />
            )}
          </Form>
        )}
      </Formik>
      {showAccessTokenGuide && (
        <ShopifyGuideModal
          onRequestClose={() => setShowAccessTokenGuide(false)}
        />
      )}
      {showBlogIdGuide && (
        <ShopifyBlogIdGuideModal
          onRequestClose={() => setShowBlogIdGuide(false)}
        />
      )}
    </>
  );
};

export default Shopify;
